import { optionsFromCapabilities } from 'ol/source/WMTS'

import type { ConfigLayerCapabilityOptions } from './ConfigLayerCapability'
import type { WMTSCapability, WMTSLayer } from './ConfigLayerCapabilityWMTS'
import type { ConfigLayerOptions } from '../ConfigLayer'
import type { Options as OLSourceWMTSOptions } from 'ol/source/WMTS'

import * as PoziUtils from 'api/utils'

import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerWMTSOptions extends ConfigLayerOptions {
  parent: string
  config: {
    url: string
    proxyHeaders?: Record<string, string>
    proxyUrl?: string
    wmts: {
      crossOrigin?: string
      format?: string
      layer?: string
      matrixSet?: string
      optionsFromCapabilities?: OLSourceWMTSOptions | null
      projection?: string
      style?: string
      tileSize?: number
      url?: string
    }
  }
  about: {
    description?: string
    organisation?: string
    source?: string
    url?: string
    urlLabel?: string
  }
  legend: string
  opacity?: number
}

export class ConfigLayerWMTS {
  static getLayer (capabilityOptions: ConfigLayerCapabilityOptions, wmtsCapability: WMTSCapability, layer: WMTSLayer, index: number): ConfigLayerWMTSOptions {
    // console.debug('ConfigLayerWMTS - getLayer', { ...capabilityOptions }, wmtsCapability, layer)

    const source = this.getSource(wmtsCapability)
    const legend = this.getLegend(capabilityOptions)
    const opacity = this.getOpacity(capabilityOptions, layer)
    let url = this.getUrl(wmtsCapability)

    const proxyHeaders = PoziUtils.getLayerProxyHeaders(capabilityOptions)
    const proxyUrl = PoziUtils.getLayerProxyUrl(capabilityOptions)
    if (url && proxyUrl) {
      url = `${proxyUrl}${url}`
    }

    const organisation = this.getOrganisation(wmtsCapability)
    const description = this.getDescription(wmtsCapability)
    const aboutUrl = this.getAboutUrl(wmtsCapability)
    const aboutUrlLabel = this.getAboutUrlLabel(wmtsCapability)

    const group = capabilityOptions.group
    const groupid = capabilityOptions.groupid
    const type = ConfigLayerType.WMTS

    // Add proxy to all URLs coming from optionsFromCapabilities
    const optionsFromCapabilities = this.getOptions(wmtsCapability, layer)
    const optionsUrls = optionsFromCapabilities?.urls
    if (optionsUrls && proxyUrl) {
      const proxiedUrls = optionsUrls.map(url => `${proxyUrl}${url}`)
      optionsFromCapabilities.urls = proxiedUrls
    }

    // Try to get the layer title from its entry in the catalogue first. If that fails, we'll try to
    // get title from optionsFromCapabilities (usually the 'identifier'). And, if that fails,
    // we'll resort to using the catalogue layer name with an index to guarantee its uniqueness
    const title = this.getTitle(layer) || optionsFromCapabilities?.layer || `${capabilityOptions.title}_${index}`
    const titleid = PoziUtils.getPoziIDFromString(title)

    // Whats here currently always gets requested for layers that have it as
    // its parent. So, when a layer is not queryable, don't set the parent to that.
    const parent = ''

    const layerConf: ConfigLayerWMTSOptions = {
      parent, // TODO: This is a workaround until https://trello.com/c/NuPIDgSL/18-enable-wmts-layers-getfeatureinfo-results-to-be-displayed-in-info-panel-by-default
      title,
      titleid,
      group,
      groupid,
      type,
      config: {
        url,
        proxyHeaders,
        proxyUrl,
        wmts: {
          optionsFromCapabilities
        }
      },
      about: {
        description,
        source,
        organisation,
        url: aboutUrl,
        urlLabel: aboutUrlLabel
      },
      legend,
      linkedLayers: capabilityOptions.linkedLayers,
      localDataSource: capabilityOptions.localDataSource,
      profiles: capabilityOptions.profiles,
      opacity,
      serverType: capabilityOptions.serverType,
      showInLayerControl: capabilityOptions.showInLayerControl,
      showLegend: capabilityOptions.showLegend
    }

    // console.debug('ConfigLayerWMTS - layerConf', titleid, { ...layerConf })

    return layerConf
  }

  private static getAboutUrl (wmtsCapability: WMTSCapability) {
    const aboutUrl = wmtsCapability.ServiceProvider?.ProviderSite || ''

    // console.debug('getAboutUrl', aboutUrl)

    return aboutUrl
  }

  private static getAboutUrlLabel (wmtsCapability: WMTSCapability) {
    const aboutUrlLabel = wmtsCapability.ServiceProvider?.ProviderName || ''

    // console.debug('getAboutUrlLabel', aboutUrlLabel)

    return aboutUrlLabel
  }

  private static getDescription (wmtsCapability: WMTSCapability) {
    const description: string = wmtsCapability.ServiceIdentification?.Abstract || ''

    // console.debug('getDescription', description)

    return description
  }

  private static getIdentifier (wmtsLayer: WMTSLayer): string {
    const identifier: string = wmtsLayer?.Identifier

    // console.debug('getIdentifier: ', identifier)

    return identifier
  }

  private static getLegend (capabilityOptions: ConfigLayerCapabilityOptions): string {
    const legend = capabilityOptions?.legend || ''

    // console.debug('getLegend: ', legend)

    return legend
  }

  private static getMatrixSet (wmtsLayer: WMTSLayer): string {
    const matrixSet: string = wmtsLayer.TileMatrixSetLink?.[0]?.TileMatrixSet || ''

    // console.debug('getMatrixSet', matrixSet)

    return matrixSet
  }

  private static getOpacity (capabilityOptions: ConfigLayerCapabilityOptions, layer: WMTSLayer) {
    const opacity = capabilityOptions.opacity ?? 1

    // console.debug('getOpacity: ', opacity, layer)

    return opacity
  }

  private static getOptions (wmtsCapability: WMTSCapability, layer: WMTSLayer) {
    // console.debug('ConfigLayerWMTS - getOptions', wmtsCapability, layer)

    let options

    const layerIdentifier = this.getIdentifier(layer)
    const matrixSet = this.getMatrixSet(layer)

    try {
      // console.debug('WMTS trying to get optionsFromCapabilities', layerIdentifier, matrixSet)

      // Use the convenience function that OL provides for extracting the options
      options = optionsFromCapabilities(wmtsCapability, {
        layer: layerIdentifier,
        matrixSet
      })

      // console.debug('WMTS optionsFromCapabilities result', options)
    } catch (error) {
      console.warn('ConfigLayerWMTS - getOptions error', error)
    }

    if (!options) {
      console.warn('ConfigLayerWMTS - getOptions - no options returned for layer', layer)
    }

    // console.debug('ConfigLayerWMTS - getOptions', layer, options)

    return options
  }

  private static getOrganisation (wmtsCapability: WMTSCapability) {
    const organisation: string = wmtsCapability.ServiceProvider?.ProviderName || ''

    // console.debug('getOrganisation', organisation)

    return organisation
  }

  private static getSource (wmtsCapability: WMTSCapability): string {
    const source: string = wmtsCapability.ServiceIdentification?.Title || ''

    // console.debug('getSource: ', source)

    return source
  }

  private static getTitle (layer: WMTSLayer): string {
    const title = String(layer?.Title)

    // console.debug('getTitle: ', title)

    return title
  }

  private static getUrl (wmtsCapability: WMTSCapability) {
    // @ts-expect-error
    const url = wmtsCapability.OperationsMetadata?.GetTile?.DCP?.HTTP?.Get?.[0]?.href || ''

    // console.debug('getUrl: ', url)

    return url
  }
}
